import i18n from '@/utils/i18n';
import React from 'react';
import { Analytics } from '@/utils/analytics';
import {
  APP_MODAL_ROUTES,
  NEW_ORDER_ROUTES,
  PAYMENT_ROUTES,
} from '@/navigators/routes';
import {
  AppModalStackParams,
  NewOrderStackParams,
  PaymentsStackParams,
} from '@/navigators/types';
import { CompositeNavigationProp, RouteProp } from '@react-navigation/native';
import {
  isPartialActive,
  usePaymentFlowStore,
} from '@/stores/use-payment-flow-store';
import { PageContainer } from '@/components/page-container';
import { partnerSupplier } from '@/utils/environment';
import { PaymentMethodSelector } from '@/components/payments/payment-method-selector';
import { PaymentMethodType } from '@/enums';
import { StackNavigationProp } from '@react-navigation/stack';
import { TabCardDetails } from '@/components/payments/types';
import { TabCardEnrollmentBanner } from '@/components/payments/tab-card-enrollment-banner';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useInitiatePlaid } from '@/hooks/use-initiate-plaid';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useToasts } from '@/contexts/toast-context';
import { useWalletNavigation } from '@/hooks/payments/use-wallet-navigation';

interface Props {
  navigation: CompositeNavigationProp<
    StackNavigationProp<PaymentsStackParams, PAYMENT_ROUTES.PaymentMethod>,
    CompositeNavigationProp<
      StackNavigationProp<
        AppModalStackParams,
        APP_MODAL_ROUTES.AddPaymentMethod
      >,
      StackNavigationProp<NewOrderStackParams, NEW_ORDER_ROUTES.PaymentMethod>
    >
  >;
  route:
    | RouteProp<PaymentsStackParams, PAYMENT_ROUTES.PaymentMethod>
    | RouteProp<AppModalStackParams, APP_MODAL_ROUTES.AddPaymentMethod>
    | RouteProp<NewOrderStackParams, NEW_ORDER_ROUTES.PaymentMethod>;
}

export const PaymentMethodSelectScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const {
    paymentMethods,
    plaidEnabled,
    refetchPaymentContext,
    setPaymentContextState,
    transactionFeeMap,
  } = usePaymentsContext();
  const {
    supplierEmployeeRole,
    selectedRestaurantRelationship: { employeeRole },
  } = useAuthenticatedUserContext();
  const { showErrorToast } = useToasts();
  const {
    navigateToAddPAD,
    navigateToAddPaymentMethod,
    navigateToTabCardApplication,
  } = useWalletNavigation();

  const rawTabCardConfig = useDynamicConfigValue('tab_card_config');
  const tabCardDetails: TabCardDetails | null =
    rawTabCardConfig?.enabled &&
    rawTabCardConfig?.sign_up_url &&
    rawTabCardConfig?.cashback_percentage !== null &&
    rawTabCardConfig?.cashback_percentage !== undefined
      ? {
          cashbackPercentage: rawTabCardConfig.cashback_percentage,
          distributorName: partnerSupplier.fullName,
          ...(rawTabCardConfig?.image_url && {
            imageUrl: rawTabCardConfig.image_url,
          }),
          signUpUrl: rawTabCardConfig.sign_up_url,
        }
      : null;
  const hasTabPaymentMethod = paymentMethods.some(
    (pm) => pm.type === PaymentMethodType.TAB,
  );
  const showTabBanner =
    Boolean(tabCardDetails) &&
    !hasTabPaymentMethod &&
    Boolean(employeeRole || supplierEmployeeRole);

  const {
    isDistributorUser,
    klarnaPaymentMethods,
    onAddPADPaymentMethod,
    onAddUnsavedPaymentMethod,
    onBeforeBackPress,
    onPayByCheck,
    onRecordCashPayment,
    onRecordCheckPayment,
    onSavePaymentMethod,
    onSelectKlarna,
    onSelectPaymentMethod,
    onAfterSavePaymentMethod,
    requireSavedPaymentMethod,
  } = route.params;

  // Route params can be stale on navigation, so make sure we have a fresh value.
  const isPartialPayActive = usePaymentFlowStore((s) =>
    isPartialActive(s.flow),
  );

  const initiatePlaid = useInitiatePlaid();
  const handleInitiatePlaid = async () => {
    try {
      await initiatePlaid();

      // Once the browser is closed, refresh the payment
      // methods in case a new one was added for Plaid
      refetchPaymentContext();
    } catch (e) {
      showErrorToast();
    }
  };

  return (
    <PageContainer
      navigationHeaderProps={{
        centerTitle: true,
        onBackPress: () => {
          onBeforeBackPress?.();
          navigation.goBack();
        },
        showBackButton: true,
        showShadow: false,
        title: i18n.t('wallet'),
      }}
    >
      <PaymentMethodSelector
        footerBanner={
          showTabBanner && tabCardDetails ? (
            <TabCardEnrollmentBanner
              onPress={() => {
                Analytics.track('wallet.tab_card_banner.press');
                navigateToTabCardApplication(tabCardDetails);
              }}
              tabCardDetails={tabCardDetails}
            />
          ) : null
        }
        isDistributorUser={isDistributorUser}
        klarnaPaymentMethods={klarnaPaymentMethods}
        onAddMethod={() =>
          navigateToAddPaymentMethod({
            onAddUnsavedPaymentMethod: onAddUnsavedPaymentMethod
              ? onAddUnsavedPaymentMethod
              : (paymentJson) => {
                  navigation.navigate(PAYMENT_ROUTES.Base, {
                    unsavedPaymentMethodJson: paymentJson,
                  });
                },
            onSavePaymentMethod: onSavePaymentMethod
              ? onSavePaymentMethod
              : () => {
                  onAfterSavePaymentMethod?.();
                  navigation.navigate(PAYMENT_ROUTES.Base);
                },
            requireSavedPaymentMethod,
            transactionFeeMap,
          })
        }
        onAddPAD={() => navigateToAddPAD({ onAddPADPaymentMethod })}
        onConnectBankAccount={plaidEnabled ? handleInitiatePlaid : undefined}
        onPayByCheck={onPayByCheck}
        onRecordCashPayment={onRecordCashPayment}
        onRecordCheckPayment={onRecordCheckPayment}
        onSelectKlarna={
          isPartialPayActive || !onSelectKlarna
            ? undefined
            : (variant) => {
                onSelectKlarna(variant);
                setPaymentContextState({ autoShowModal: true });
                navigation.goBack();
              }
        }
        onSelectMethod={
          onSelectPaymentMethod
            ? (paymentMethod) => {
                onSelectPaymentMethod(paymentMethod);
                navigation.goBack();
              }
            : undefined
        }
      />
    </PageContainer>
  );
};
